
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import QRCode from '@/pages/QRCode';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { updateMerchantProfile } from '@/services/merchantService';
import { Link } from 'react-router-dom';
import { Store, Pencil, Save, Globe, MapPin, Share2 } from 'lucide-react';

interface MerchantProfileTabProps {
  userId: string;
  merchantInfo: any;
  onProfileUpdate: () => void;
}

const MerchantProfileTab: React.FC<MerchantProfileTabProps> = ({ 
  userId, 
  merchantInfo,
  onProfileUpdate
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    business_name: '',
    description: '',
    address: '',
    website_url: '',
    logo_path: '',
    latitude: 0,
    longitude: 0
  });
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);

  useEffect(() => {
    if (merchantInfo) {
      setFormData({
        business_name: merchantInfo.business_name || '',
        description: merchantInfo.description || '',
        address: merchantInfo.address || '',
        website_url: merchantInfo.website_url || '',
        logo_path: merchantInfo.logo_path || '',
        latitude: merchantInfo.latitude || 0,
        longitude: merchantInfo.longitude || 0
      });
    }
  }, [merchantInfo]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setLogoFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleGetLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setFormData(prev => ({
            ...prev,
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          }));
          toast.success('Location updated');
        },
        (error) => {
          console.error('Error getting location:', error);
          toast.error('Could not get location');
        }
      );
    } else {
      toast.error('Geolocation is not supported by this browser');
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      
      if (!formData.business_name) {
        toast.error('Business name is required');
        return;
      }
      
      // If no merchant record exists yet, create one
      if (!merchantInfo) {
        const { data, error } = await supabase
          .from('merchants')
          .insert({
            merchant_id: userId,
            business_name: formData.business_name,
            description: formData.description,
            address: formData.address,
            website_url: formData.website_url,
            latitude: formData.latitude || null,
            longitude: formData.longitude || null,
            location: JSON.stringify({ lat: formData.latitude, lng: formData.longitude })
          })
          .select()
          .single();
          
        if (error) {
          throw error;
        }
        
        toast.success('Merchant profile created');
        onProfileUpdate();
      } else {
        // Update existing merchant profile with fixed location field
        const result = await updateMerchantProfile(
          userId,
          {
            business_name: formData.business_name,
            description: formData.description,
            address: formData.address,
            website_url: formData.website_url,
            latitude: formData.latitude || null,
            longitude: formData.longitude || null,
            location: JSON.stringify({ lat: formData.latitude, lng: formData.longitude })
          },
          logoFile
        );
        
        if (result.success) {
          toast.success('Merchant profile updated');
          onProfileUpdate();
        } else {
          throw new Error(result.error);
        }
      }
      
      setIsEditing(false);
    } catch (error) {
      console.error('Error saving merchant profile:', error);
      toast.error('Failed to save merchant profile');
    } finally {
      setLoading(false);
    }
  };

  if (!merchantInfo && !isEditing) {
    return (
      <Card className="bg-gray-900 text-white">
        <CardHeader>
          <CardTitle>Merchant Profile</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col items-center">
          <Store className="h-16 w-16 mb-4 text-yellow-600" />
          <p className="mb-4 text-center">You don't have a merchant profile yet.</p>
          <Button onClick={() => setIsEditing(true)}>
            Create Merchant Profile
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card className="bg-gray-900 text-white">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Merchant Profile</CardTitle>
          {!isEditing ? (
            <Button variant="outline" size="sm" onClick={() => setIsEditing(true)}>
              <Pencil className="h-4 w-4 mr-2" /> Edit Profile
            </Button>
          ) : (
            <Button variant="outline" size="sm" onClick={() => {
              setIsEditing(false);
              if (merchantInfo) {
                setFormData({
                  business_name: merchantInfo.business_name || '',
                  description: merchantInfo.description || '',
                  address: merchantInfo.address || '',
                  website_url: merchantInfo.website_url || '',
                  logo_path: merchantInfo.logo_path || '',
                  latitude: merchantInfo.latitude || 0,
                  longitude: merchantInfo.longitude || 0
                });
              }
              setLogoPreview(null);
              setLogoFile(null);
            }}>
              Cancel
            </Button>
          )}
        </CardHeader>
        
        {isEditing ? (
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="business_name">Business Name*</Label>
              <Input
                id="business_name"
                name="business_name"
                value={formData.business_name}
                onChange={handleInputChange}
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows={3}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="address">Address</Label>
              <Input
                id="address"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="website_url">Website URL</Label>
              <Input
                id="website_url"
                name="website_url"
                value={formData.website_url}
                onChange={handleInputChange}
                type="url"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="logo">Logo</Label>
              <div className="flex items-center space-x-4">
                {(logoPreview || formData.logo_path) && (
                  <div className="w-16 h-16 overflow-hidden rounded">
                    <img 
                      src={logoPreview || formData.logo_path} 
                      alt="Logo preview" 
                      className="w-full h-full object-cover" 
                    />
                  </div>
                )}
                <Input
                  id="logo"
                  type="file"
                  accept="image/*"
                  onChange={handleLogoChange}
                  className="max-w-xs"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Store Location</Label>
              <div className="flex space-x-4">
                <div className="flex-1">
                  <Label htmlFor="latitude">Latitude</Label>
                  <Input
                    id="latitude"
                    name="latitude"
                    value={formData.latitude || ''}
                    onChange={handleInputChange}
                    type="number"
                    step="any"
                  />
                </div>
                <div className="flex-1">
                  <Label htmlFor="longitude">Longitude</Label>
                  <Input
                    id="longitude"
                    name="longitude"
                    value={formData.longitude || ''}
                    onChange={handleInputChange}
                    type="number"
                    step="any"
                  />
                </div>
                <Button 
                  type="button" 
                  variant="outline" 
                  className="self-end"
                  onClick={handleGetLocation}
                >
                  <MapPin className="h-4 w-4 mr-2" /> Get Location
                </Button>
              </div>
            </div>
            
            <Button
              onClick={handleSave}
              disabled={loading || !formData.business_name}
              className="mt-4 w-full"
            >
              {loading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" /> Save Profile
                </>
              )}
            </Button>
          </CardContent>
        ) : merchantInfo && (
          <>
            <CardContent className="space-y-4">
              <div className="flex flex-col md:flex-row">
                <div className="md:w-1/3 flex justify-center items-start">
                  {merchantInfo.logo_path ? (
                    <img 
                      src={merchantInfo.logo_path} 
                      alt={`${merchantInfo.business_name} logo`} 
                      className="w-32 h-32 object-contain rounded-lg"
                    />
                  ) : (
                    <div className="w-32 h-32 bg-gray-700 rounded-lg flex items-center justify-center">
                      <Store className="h-16 w-16 text-gray-400" />
                    </div>
                  )}
                </div>
                
                <div className="md:w-2/3 md:pl-6 mt-4 md:mt-0">
                  <h2 className="text-2xl font-bold text-white mb-2">{merchantInfo.business_name}</h2>
                  
                  {merchantInfo.description && (
                    <p className="text-gray-300 mb-4">{merchantInfo.description}</p>
                  )}
                  
                  {merchantInfo.address && (
                    <div className="flex items-center mb-2">
                      <MapPin className="w-4 h-4 text-yellow-600 mr-2" />
                      <span className="text-gray-300">{merchantInfo.address}</span>
                    </div>
                  )}
                  
                  {merchantInfo.website_url && (
                    <div className="flex items-center mb-4">
                      <Globe className="w-4 h-4 text-yellow-600 mr-2" />
                      <a href={merchantInfo.website_url} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">
                        {merchantInfo.website_url}
                      </a>
                    </div>
                  )}
                  
                  <div className="flex flex-wrap gap-2 mt-4">
                    <Button asChild className="bg-yellow-600 hover:bg-yellow-700 text-black">
                      <Link to={`/merchant/${merchantInfo.id}/record`}>
                        Record a Video
                      </Link>
                    </Button>
                    
                    <Button asChild variant="outline">
                      <Link to={`/merchant/${merchantInfo.id}`}>
                        <Share2 className="h-4 w-4 mr-2" />
                        View Public Profile
                      </Link>
                    </Button>
                  </div>
                </div>
              </div>
              
              <div className="mt-6">
                <h3 className="text-xl text-white mb-2">Merchant QR Code</h3>
                <p className="text-gray-400 text-sm mb-4">
                  Share this QR code with customers to let them record videos at your location.
                </p>
                
                <div className="bg-white p-4 rounded-lg inline-block">
                  <QRCode 
                    value={`${window.location.origin}/merchant/${merchantInfo.id}`} 
                    size={150}
                    foregroundColor="#000000"
                    backgroundColor="#FFFFFF"
                  />
                </div>
              </div>
            </CardContent>
          </>
        )}
      </Card>
    </div>
  );
};

export default MerchantProfileTab;
